import { useCallback, useMemo } from 'react';

import { CountryConfigNames } from '../../contexts/country-config-context/country-config-enums';
import { useCountryConfig } from '../../hooks/use-country-config';

export const ItemSource = {
  Carrier: 'carrier',
  DutiesTax: 'dutiestax',
};

export const ItemCategory = {
  Insurance: 'insurance',
  Shipping: 'shipping',
  Tax: 'tax',
  DutiesTax: 'duties_tax',
  PrepaidDutiesAndTaxes_Duties: 'pdt_duties',
  PrepaidDutiesAndTaxes_Tax: 'pdt_taxes',
  PrepaidDutiesAndTaxes_ImportFees: 'pdt_import',
};

const getVATPercentageFromItem = (item) => {
  try {
    const { TaxPercentage: percentage } = item?.memo ? JSON.parse(item.memo) : {};
    return percentage || 0;
  } catch {
    return 0;
  }
};

const subtractLineItemAmount = (subtotal, lineItem) => {
  if (!lineItem) return subtotal;

  const roundedSubtotal = Math.round(subtotal * 100) / 100;
  const roundedLineItemAmount = Math.round((lineItem?.amount?.value || 0) * 100) / 100;

  return roundedSubtotal - roundedLineItemAmount;
};

/**
 * Combines split duty line items into a single DutiesLineItem
 * @param {Array<{id: number, source: string, category: string, description: string, amount: {value: number, code: string}, memo: string|null}>} dutySplitItems
 * @returns {{id: number, source: string, category: string, description: string, amount: {value: number, code: string}, memo: string|null}|undefined}
 */
const combineSplitDutyItems = (dutySplitItems) => {
  if (!dutySplitItems.length) return undefined;

  const totalAmount = dutySplitItems.reduce((sum, item) => {
    return sum + (item?.amount?.value || 0);
  }, 0);

  const firstItem = dutySplitItems[0];

  return {
    ...firstItem,
    amount: {
      value: Math.round(totalAmount * 100) / 100,
      code: firstItem.amount.code,
    },
  };
};

export const useCostReviewLabelProviderLineItems = (lineItems, insuranceSubtotal, confirmationSubtotal, otherCostSubtotal, isWallet) => {
  const [isApplyTaxChargesEnabled] = useCountryConfig(CountryConfigNames.ApplyTaxCharges);

  const shouldSplitTaxCharge = useCallback(
    (item) => isWallet && isApplyTaxChargesEnabled && item.category === ItemCategory.Tax,
    [isApplyTaxChargesEnabled, isWallet],
  );

  const { additionalChargesLineItems, insuranceLineItems, VATTaxChargeLineItem, DutiesLineItem } = useMemo(() => {
    // list of DDP categories which needs to be aggregated into one cost review entry
    const dutiesTaxCategories = [
      ItemCategory.PrepaidDutiesAndTaxes_Duties,
      ItemCategory.PrepaidDutiesAndTaxes_Tax,
      ItemCategory.PrepaidDutiesAndTaxes_ImportFees,
    ];

    const result = lineItems.reduce(
      (acc, item) => {
        const isDutiesTaxSource = item.source === ItemSource.Carrier || item.source === ItemSource.DutiesTax;
        const isDutiesTaxCategory = item.category === ItemCategory.DutiesTax || dutiesTaxCategories.includes(item.category);

        if (item.source === ItemSource.Carrier) {
          if (item.category === ItemCategory.Insurance) {
            return {
              ...acc,
              insuranceLineItems: [...acc.insuranceLineItems, item],
            };
          }
          if (shouldSplitTaxCharge(item)) {
            return {
              ...acc,
              VATTaxChargeLineItem: { ...item, percentage: getVATPercentageFromItem(item) },
            };
          }
          if (item.category !== ItemCategory.Shipping && !isDutiesTaxCategory) {
            return {
              ...acc,
              additionalChargesLineItems: [...acc.additionalChargesLineItems, item],
            };
          }
        }

        if (isDutiesTaxSource && isDutiesTaxCategory) {
          if (item.category === ItemCategory.DutiesTax) {
            return {
              ...acc,
              DutiesLineItem: item,
            };
          }
          if (dutiesTaxCategories.includes(item.category)) {
            return {
              ...acc,
              dutySplitItems: [...acc.dutySplitItems, item],
            };
          }
        }

        return acc;
      },
      {
        additionalChargesLineItems: [],
        insuranceLineItems: [],
        VATTaxChargeLineItem: undefined,
        DutiesLineItem: undefined,
        dutySplitItems: [],
      },
    );

    const combinedDutiesLineItem = combineSplitDutyItems(result.dutySplitItems);
    if (combinedDutiesLineItem) {
      result.DutiesLineItem = combinedDutiesLineItem;
    }

    // Removing dutySplitItems before returning, because it's needed only internally
    const { dutySplitItems, ...publicResult } = result;
    return publicResult;
  }, [lineItems, shouldSplitTaxCharge]);

  const areAdditionalChargesProvided = !!additionalChargesLineItems.length;
  const shouldShowConfirmationSubtotal = !areAdditionalChargesProvided && confirmationSubtotal > 0;
  // We need to subtract line item amounts when they are displayed in separate sections to avoid showing them in "Additional charges" section
  const otherCostWithoutVATTaxes = subtractLineItemAmount(otherCostSubtotal, VATTaxChargeLineItem);
  const otherCostWithoutDutiesAndTaxes = subtractLineItemAmount(otherCostSubtotal, DutiesLineItem);
  const shouldShowOtherCostsSubtotal = !areAdditionalChargesProvided && otherCostWithoutVATTaxes > 0 && otherCostWithoutDutiesAndTaxes > 0;
  const shouldShowInsurance = Boolean(insuranceSubtotal > 0 || insuranceLineItems.length);

  return {
    additionalChargesLineItems,
    insuranceLineItems,
    areAdditionalChargesProvided,
    shouldShowConfirmationSubtotal,
    shouldShowOtherCostsSubtotal,
    shouldShowInsurance,
    VATTaxChargeLineItem,
    DutiesLineItem,
  };
};
